import {
	Component, Input, Output, EventEmitter, SimpleChanges, OnDestroy,
	OnChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettings, ProtocolSettingsTypes, PrtgSettings } from 'DiscoveryEngine.TS/Protocols';
import { Guid, StringUtils } from "DiscoveryEngine.TS/Utils";
import { ComponentBase, Changeable } from "common";
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { IdentityService, LicensingService } from 'identity';
import { MessageService } from 'base';
import { ToolsService } from 'tools';
import { JiraExport } from 'DiscoveryEngine.TS/Export';
import { ConfigurationService } from '../../../configuration';
import { AgentDto } from '../../../UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from '../../../DiscoveryEngine.TS/Discovery';


@Component({
	selector: 'jira-export-config',
	templateUrl: '../jira-export-config/jira-export-config.component.html',
	styleUrls: ['../jira-export-config/jira-export-config.component.scss']
})
export class JiraExportConfigComponent extends ComponentBase implements OnDestroy, OnChanges, Changeable {

	@Input()
	public Network: NetViewDto;

	@Input()
	public JiraExport: JiraExport;

	@Input()
	public Agent: AgentDto;

	@Input()
	public ScheduledDiscovery: ScheduledDiscovery;

	@Input()
	public EnableExportOrigin: boolean;

	public SettingsForm: UntypedFormGroup;

	public EnableExport: UntypedFormControl;

	public TargetServerURL: UntypedFormControl;
	public TargetServerUsername: UntypedFormControl;
	public TargetApiToken: UntypedFormControl;
	public TargetObjectSchema: UntypedFormControl;

	public ExportOrigin: UntypedFormControl;
	public ExportDevices: UntypedFormControl;

	private apiTokenChanged: boolean;
	private apiTokenChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private configService: ConfigurationService,
		private toolsService: ToolsService) {

		super();

		this.Network = null;
		this.JiraExport = null;
		this.EnableExportOrigin = false;
	}

	public ngOnDestroy(): void {
		if (this.apiTokenChangedSub) {
			this.apiTokenChangedSub.unsubscribe();
		}
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	protected BuildForm(): void {

		let enableExport = false;

		// Jira settings
		let serverURL = "";
		let serverUsername = "";
		let apiToken = "";
		let objSchema = "";

		let exportOrigin = "";

		let exportDevices = false;

		if (this.JiraExport) {
			let value = this.JiraExport;

			enableExport = value.EnableExport;

			if (value.HasTargetServerURL)
				serverURL = value.TargetServerURL;
			if (value.HasTargetServerUsername)
				serverUsername = value.TargetServerUsername;
			if (value.HasTargetApiToken)
				apiToken = this.hidePassword(value.TargetApiToken);
			if (value.HasTargetObjectSchema)
				objSchema = value.TargetObjectSchema;

			if (value.HasExportOrigin)
				exportOrigin = value.ExportOrigin;

			exportDevices = value.ExportDevices;
		}

		this.EnableExport = new UntypedFormControl(enableExport);

		this.TargetServerURL = new UntypedFormControl(serverURL);
		this.TargetServerUsername = new UntypedFormControl(serverUsername);
		this.TargetApiToken = new UntypedFormControl(apiToken);
		this.TargetObjectSchema = new UntypedFormControl(objSchema);

		this.ExportOrigin = new UntypedFormControl(exportOrigin);

		this.ExportDevices = new UntypedFormControl(exportDevices);

		this.apiTokenChanged = false;
		this.apiTokenChangedSub = this.TargetApiToken.valueChanges.subscribe(() => {
			this.apiTokenChanged = true;
		});

		this.SettingsForm = this.fb.group({
			EnableExport: this.EnableExport,
			TargetServerURL: this.TargetServerURL,
			TargetServerUsername: this.TargetServerUsername,
			TargetApiToken: this.TargetApiToken,
			TargetObjectSchema: this.TargetObjectSchema,
			ExportOrigin: this.ExportOrigin,
			ExportDevices: this.ExportDevices,
		},
		{
			validator: Validators.compose([
				this.validJiraSettings(this.EnableExport, this.TargetServerURL, this.TargetServerUsername, this.TargetApiToken, this.TargetObjectSchema)
			])
		});
	}

	public get ExportEnabled(): boolean {
		if (this.EnableExport) {
			return this.EnableExport.value;
		}
		else {
			return false;
		}
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.BuildForm();
	}

	public get tempChanges(): boolean {
		return this.SettingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.SettingsForm.valid;
	}

	public discardTempChanges(): void {
		this.BuildForm();
	}

	public applyTempChanges(): void {
		if (this.JiraExport) {
			let value = this.JiraExport;

			value.EnableExport = this.EnableExport.value;

			value.TargetServerURL = this.TargetServerURL.value;
			value.TargetServerUsername = this.TargetServerUsername.value;
			if (this.apiTokenChanged) {
				value.TargetApiToken = StringUtils.ObfuscateString2(this.TargetApiToken.value);
			}
			value.TargetObjectSchema = this.TargetObjectSchema.value;

			value.ExportOrigin = this.ExportOrigin.value;

			value.ExportDevices = this.ExportDevices.value;
			
			this.BuildForm();
		}
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiTokenChanged) {
			this.apiTokenChanged = true;

			this.TargetApiToken.setValue('');
			this.TargetApiToken.markAsDirty();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Jira Export"];
	}

	private validJiraSettings(enableExport: UntypedFormControl,
		serverURL: UntypedFormControl,
		serverUsername: UntypedFormControl,
		apiToken: UntypedFormControl,
		objSchema: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!serverURL.value) {
					return {
						validJiraSettings: { valid: false }
					};
				}
				else if (!serverUsername.value) {
					return {
						validJiraSettings: { valid: false }
					};
				}
				else if (!apiToken.value) {
					return {
						validJiraSettings: { valid: false }
					};
				}
				else if (!objSchema.value) {
					return {
						validJiraSettings: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	public ExportNow(): void {
		if (this.tempChanges) {
			this.messageService.displayInfoMessage(
				"You have unsaved changes to your Jira export settings.\nPlease save before exporting.", 10000);
		}
		else {
			if (!!this.Network && !!this.Network.Guid) {
				let sessionGuid = this.identityService.sessionGuid;
				let netViewGuid = this.Network.Guid;
				let agentGuid = this.navService.location.agentGuid;
				let schedDiscGuid = this.navService.location.schedDiscGuid;
				this.discResultsService.runJiraExport(sessionGuid, netViewGuid, agentGuid, schedDiscGuid).subscribe(
					(result: boolean) => {
						this.messageService.displayInfoMessage("Jira Export has been initiated");
					},
					(error) => {
						this.messageService.displayErrorMessage("Jira Export failed");
					});
			}
		}
	}

	public get enableTestJiraButton(): boolean {
		return (!!this.TargetServerURL && !!this.TargetServerURL.value
			&& !!this.TargetServerUsername && !!this.TargetServerUsername.value
			&& !!this.TargetApiToken && !!this.TargetApiToken.value);
	}

	public testJiraConnect(): void {
		let apiToken = "";
		if (this.apiTokenChanged) {
			apiToken = StringUtils.ObfuscateString2(this.TargetApiToken.value);
		}
		else {
			apiToken = this.JiraExport.TargetApiToken;
		}
		this.configService.testJiraConnection(this.TargetServerURL.value, this.TargetServerUsername.value, apiToken).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Jira");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Jira");
			});
	}
}
